import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

/**
 * Muestra el informe en un iframe aislado de los estilos del ERP.
 * El mismo iframe se usa para imprimir / guardar como PDF.
 */
const ReportFrame = forwardRef(function ReportFrame({ html, title }, ref) {
  const frameRef = useRef(null);

  const ajustarAltura = useCallback(() => {
    const f = frameRef.current;
    const doc = f?.contentDocument;
    if (!doc?.documentElement) return;
    f.style.height = `${doc.documentElement.scrollHeight}px`;
  }, []);

  useEffect(() => {
    const f = frameRef.current;
    const doc = f?.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    ajustarAltura();
    const t = setTimeout(ajustarAltura, 400);
    doc.fonts?.ready?.then(ajustarAltura).catch(() => {});
    return () => clearTimeout(t);
  }, [html, ajustarAltura]);

  useEffect(() => {
    window.addEventListener("resize", ajustarAltura);
    return () => window.removeEventListener("resize", ajustarAltura);
  }, [ajustarAltura]);

  useImperativeHandle(ref, () => ({
    async print() {
      const w = frameRef.current?.contentWindow;
      if (!w) return;
      try {
        await w.document.fonts?.ready;
      } catch {
        /* sin fuentes web: se imprime con las del sistema */
      }
      const prev = document.title;
      document.title = title || prev;
      w.focus();
      w.print();
      setTimeout(() => {
        document.title = prev;
      }, 1500);
    },
  }), [title]);

  return (
    <iframe
      ref={frameRef}
      title="Vista previa del informe"
      className="w-full block border-0 rounded-sm"
      style={{ minHeight: 600 }}
      data-testid="informe-preview"
    />
  );
});

export default ReportFrame;
